import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Animated,
  Dimensions,
  Keyboard,
  Platform,
} from 'react-native';
import SystemNavigationBar from 'react-native-system-navigation-bar';
import { showConfirm, showAlert } from '@/utils/common/alert';
import { KeyboardDismissWrapper } from '@/components/common/KeyboardDismissWrapper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { SVG_COMPONENTS, SVG_DIMENSIONS } from '@/assets/admin';
import { AdminResioLogo } from '@/assets/admin/AdminResioLogo';
import { ADMIN_COLORS, FUNDING_PATH } from '@/constants/admin';
import { INavigationItem } from '@/types/admin/navigation';
import { PropertyOrgSelectors } from './PropertyOrgSelectors';
import { useAdminNavigation } from '@/hooks/admin/useAdminNavigation';
import { AdminNavList } from './AdminNavList';
import { useDisplaySelectors } from '@/hooks/admin/useDisplaySelectors';
import { useAppDispatch, useAppSelector } from '@/store';
import { 
  setSelectedOrganizationId, 
  setSelectedPropertyId,
  setIsMenuOpen,
  setExpandedMenuIds,
} from '@/store/slices/admin/adminUISlice';
import { selectIsMenuOpen } from '@/store/selectors/adminUISelectors';
import { ADMIN_PATH_TO_SCREEN } from '@/utils/admin/navigation';
import { ADMIN_NAVIGATION_ITEMS } from '@/constants/admin/adminNavigation';

const { height: SCREEN_HEIGHT } = Dimensions.get('window');

const getMenuIdForPath = (path: string): string | null => {
  for (const item of ADMIN_NAVIGATION_ITEMS) {
    if (item.path === path) return item.id;
    if (item.subNavItems?.some(sub => sub.path === path)) return item.id;
  }
  return null;
};

interface AdminHeaderWithMenuProps {
  onLogoPress?: () => void;
  onSignOut?: () => void;
  onNavigate?: (path: string) => void;
  currentPath?: string;
}

export const AdminHeaderWithMenu: React.FC<AdminHeaderWithMenuProps> = ({
  onLogoPress,
  onSignOut,
  onNavigate,
  currentPath = '/properties',
}) => {
  const dispatch = useAppDispatch();
  const insets = useSafeAreaInsets();
  const [animationError, setAnimationError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  
  // Get state from Redux
  const isMenuOpen = useAppSelector(selectIsMenuOpen);
  
  // Use real data from hooks instead of props
  const { navigationItems, adminUser, isPathActive, getFilteredSubNavItems } = useAdminNavigation();
  const displaySelectors = useDisplaySelectors(currentPath);
  const isFunding = useMemo(() => currentPath.includes(FUNDING_PATH), [currentPath]);
  
  // Calculate total header height including safe area
  const totalHeaderHeight = insets.top + 74;
  
  // Animation values - adjusted for menu starting below header + safe area
  const slideAnim = useRef(new Animated.Value(-(SCREEN_HEIGHT - totalHeaderHeight))).current;
  const overlayOpacity = useRef(new Animated.Value(0)).current;

  // Cleanup animations on unmount and ensure menu state is reset on mount
  useEffect(() => {
    isMountedRef.current = true;
    
    // Reset menu state on mount to prevent stale state from previous screen
    dispatch(setIsMenuOpen(false));
    
    return () => {
      isMountedRef.current = false;
      slideAnim.removeAllListeners();
      overlayOpacity.removeAllListeners();
    };
  }, [slideAnim, overlayOpacity, dispatch]);

  const handleAnimationError = useCallback((error: string) => {
    console.error('Animation error:', error);
    setAnimationError(error);
    
    // Reset menu state on animation failure
    setIsMenuOpen(false);
    slideAnim.setValue(-(SCREEN_HEIGHT - totalHeaderHeight));
    overlayOpacity.setValue(0);
  }, [slideAnim, overlayOpacity, totalHeaderHeight]);

  // Close menu with animation and wait for completion
  const closeMenuWithAnimation = useCallback((): Promise<void> => {
    if (Platform.OS === 'android') {
      SystemNavigationBar.setNavigationColor(ADMIN_COLORS.BACKGROUND.SECONDARY);
    }
    return new Promise((resolve) => {
      // Always run the animation to close the visual menu, regardless of state
      // This handles cases where state was reset but visual menu is still open
      try {
        // Close menu - slide up and fade out
        Animated.parallel([
          Animated.timing(slideAnim, {
            toValue: -(SCREEN_HEIGHT - totalHeaderHeight),
            duration: 300,
            useNativeDriver: true,
          }),
          Animated.timing(overlayOpacity, {
            toValue: 0,
            duration: 300,
            useNativeDriver: true,
          }),
        ]).start(({ finished }) => {
          // Animation complete - just clear any animation errors
          // State management is handled elsewhere (immediately during navigation)
          if (isMountedRef.current) {
            setAnimationError(null);
          }
          resolve();
        });
      } catch (error) {
        console.error('Error closing menu animation:', error);
        if (isMountedRef.current) {
          dispatch(setIsMenuOpen(false));
        }
        resolve();
      }
    });
  }, [slideAnim, overlayOpacity, totalHeaderHeight, dispatch]);

  const toggleMenu = useCallback(() => {
    try {
      // Dismiss keyboard when menu is toggled
      Keyboard.dismiss();
      
      if (isMenuOpen) {
        // Close menu - slide up and fade out
        if (Platform.OS === 'android') {
          SystemNavigationBar.setNavigationColor(ADMIN_COLORS.BACKGROUND.SECONDARY);
        }
        Animated.parallel([
          Animated.timing(slideAnim, {
            toValue: -(SCREEN_HEIGHT - totalHeaderHeight),
            duration: 300,
            useNativeDriver: true,
          }),
          Animated.timing(overlayOpacity, {
            toValue: 0,
            duration: 300,
            useNativeDriver: true,
          }),
        ]).start(({ finished }) => {
          if (isMountedRef.current) {
            dispatch(setIsMenuOpen(false));
            setAnimationError(null);
          }
        });
      } else {
        // Open menu - slide down and fade in
        if (Platform.OS === 'android') {
          SystemNavigationBar.setNavigationColor(ADMIN_COLORS.NAVIGATION.BACKGROUND);
        }
        dispatch(setIsMenuOpen(true));
        Animated.parallel([
          Animated.timing(slideAnim, {
            toValue: 0,
            duration: 300,
            useNativeDriver: true,
          }),
          Animated.timing(overlayOpacity, {
            toValue: 1,
            duration: 300,
            useNativeDriver: true,
          }),
        ]).start(({ finished }) => {
          if (isMountedRef.current) {
            setAnimationError(null);
          }
        });
      }
    } catch (error) {
      handleAnimationError(`Animation failed: ${error}`);
    }
  }, [isMenuOpen, slideAnim, overlayOpacity, totalHeaderHeight, handleAnimationError]);

  const handleSignOut = useCallback(async () => {
    const confirmed = await showConfirm(
      'Sign Out',
      'Are you sure you want to sign out?',
      'Sign Out',
      'Cancel'
    );
    
    if (!confirmed) return;
    
    // Close menu with animation and wait for completion
    await closeMenuWithAnimation();
    // Now call the logout function
    onSignOut?.();
  }, [closeMenuWithAnimation, onSignOut]);

  const handleNavigation = useCallback((path: string) => {
    try {
      dispatch(setIsMenuOpen(false));

      const activeMenuId = getMenuIdForPath(path);
      dispatch(setExpandedMenuIds(activeMenuId ? [activeMenuId] : []));

      onNavigate?.(path);

      closeMenuWithAnimation().catch((error) => {
        console.error('Menu close animation error:', error);
      });
    } catch (error) {
      console.error('Navigation error:', error);
      showAlert('Navigation Error', 'Failed to navigate. Please try again.');
    }
  }, [dispatch, closeMenuWithAnimation, onNavigate]);

  // Handle organization and property selection changes
  const handleOrganizationChange = useCallback((orgId: number) => {
    dispatch(setSelectedOrganizationId(orgId));
    
    // Auto-select first available property for the new organization
    const availableProperty = adminUser.properties?.find(prop => prop.organizationId === orgId);
    if (availableProperty) {
      dispatch(setSelectedPropertyId(availableProperty.id));
    }
  }, [dispatch, adminUser.properties]);

  const handlePropertyChange = useCallback((propId: number) => {
    dispatch(setSelectedPropertyId(propId));
  }, [dispatch]);



  const renderNavMenuItem = useCallback((item: INavigationItem) => null, []);

  // Show error state if animation fails
  if (animationError) {
    return (
      <View style={[styles.safeArea, { paddingTop: insets.top }]}>
        <View style={styles.header}>
          <Text style={styles.errorText}>Menu Error: {animationError}</Text>
          <TouchableOpacity
            onPress={() => setAnimationError(null)}
            style={styles.retryButton}
          >
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <>
      {/* Fixed Header */}
      <View style={[styles.safeArea, { paddingTop: insets.top }]}>
        <KeyboardDismissWrapper style={styles.header}>
          {/* Left Side - Resio Logo */}
          <TouchableOpacity 
            style={styles.leftContainer}
            onPress={onLogoPress}
            activeOpacity={0.7}
            accessibilityLabel="Resio logo"
            accessibilityHint="Tap to go to home screen"
            accessibilityRole="button"
          >
            <AdminResioLogo />
          </TouchableOpacity>

          {/* Right Side - Menu */}
          <TouchableOpacity 
            style={styles.menuContainer}
            onPress={toggleMenu}
            activeOpacity={0.7}
            accessibilityLabel={isMenuOpen ? "Close menu" : "Open menu"}
            accessibilityHint={isMenuOpen ? "Tap to close navigation menu" : "Tap to open navigation menu"}
            accessibilityRole="button"
            accessibilityState={{ expanded: isMenuOpen }}
          >
            {!isMenuOpen && <Text style={styles.menuText}>Menu</Text>}
            {isMenuOpen ? (
              <SVG_COMPONENTS.CANCEL_X {...SVG_DIMENSIONS.CANCEL_X} />
            ) : (
              <SVG_COMPONENTS.HAMBURGER {...SVG_DIMENSIONS.HAMBURGER} />
            )}
          </TouchableOpacity>
        </KeyboardDismissWrapper>
      </View>

      {/* Menu Overlay */}
      {isMenuOpen && (
        <Animated.View 
          style={[
            styles.menuOverlay,
            {
              top: totalHeaderHeight,
              opacity: overlayOpacity,
              transform: [{ translateY: slideAnim }],
            }
          ]}
          accessibilityViewIsModal={true}
        >
          {/* Scrollable area: selectors + nav list */}
          <ScrollView
            style={styles.menuScrollView}
            contentContainerStyle={styles.menuScrollContent}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {/* Organization/Property Selectors */}
            <View style={styles.orgPropSection}>
              <PropertyOrgSelectors
                organizations={adminUser.organizations || []}
                properties={adminUser.properties || []}
                selectedOrganizationId={adminUser.selectedOrganizationId}
                selectedPropertyId={adminUser.selectedPropertyId}
                onOrganizationChange={handleOrganizationChange}
                onPropertyChange={handlePropertyChange}
                showPropertySelector={!isFunding && displaySelectors.includes('property')} // Hide on Funding
                disableOrganization={!displaySelectors.includes('organization')}
                disabled={false}
              />
            </View>

            {/* Navigation Menu Items */}
            <View style={styles.navMenu}>
              <AdminNavList
                currentPath={currentPath}
                onNavigate={handleNavigation}
              />
            </View>
          </ScrollView>

          {/* Bottom Section - Fixed at bottom */}
          <View style={styles.bottomSection}>
            <Text style={styles.toggleText}>
              Learn more about us at{' '}
              <Text style={styles.resioLink}>RESIO.COM</Text>
            </Text>
            
            <TouchableOpacity
              style={styles.signOutButton}
              onPress={handleSignOut}
              activeOpacity={0.8}
              accessibilityLabel="Sign out"
              accessibilityHint="Tap to sign out of your admin account"
              accessibilityRole="button"
            >
              <Text style={styles.signOutText} numberOfLines={1}>Sign out</Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  // Header styles - Following Figma specs exactly
  safeArea: {
    backgroundColor: ADMIN_COLORS.HEADER.BACKGROUND,
    zIndex: 1000,
  },
  header: {
    width: '100%',
    height: 74,
    backgroundColor: ADMIN_COLORS.HEADER.BACKGROUND,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
  },
  leftContainer: {
    width: 101.375,
    height: 34,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  menuContainer: {
    width: 82,
    height: 34,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 20,
  },
  menuText: {
    fontFamily: 'System',
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 21,
    letterSpacing: 0,
    textAlign: 'right',
    color: ADMIN_COLORS.HEADER.MENU_TEXT,
    width: 37,
    height: 21,
  },
  
  // Error handling styles
  errorText: {
    color: ADMIN_COLORS.TEXT.ERROR,
    fontSize: 12,
    flex: 1,
  },
  retryButton: {
    backgroundColor: ADMIN_COLORS.INTERACTIVE.PRIMARY,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
  },
  retryButtonText: {
    color: ADMIN_COLORS.TEXT.PRIMARY,
    fontSize: 12,
    fontWeight: '600',
  },
  
  // Menu overlay styles - positioned below header + safe area
  menuOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: ADMIN_COLORS.NAVIGATION.BACKGROUND,
    zIndex: 999,
    flexDirection: 'column',
    // Ensure dropdowns can render outside overlay bounds
    overflow: 'visible',
  },
  
  // Organization/Property section styles - Following Figma specs exactly
  orgPropSection: {
    width: '100%',
    backgroundColor: ADMIN_COLORS.NAVIGATION.BACKGROUND,
    paddingTop: 12,
    paddingRight: 20,
    paddingBottom: 10,
    paddingLeft: 20,
    gap: 20,
    overflow: 'visible',
  },
  selectorContainer: {
    width: '100%',
    height: 56,
  },
  selectorInput: {
    width: '100%',
    height: 56,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.SELECTOR.BORDER,
    position: 'relative',
  },
  labelContainer: {
    position: 'absolute',
    top: -6,
    left: 8,
    height: 12,
    backgroundColor: ADMIN_COLORS.SELECTOR.BACKGROUND,
    paddingHorizontal: 4,
    zIndex: 1,
  },
  labelText: {
    fontFamily: 'System',
    fontWeight: '400',
    fontSize: 12,
    lineHeight: 12,
    color: ADMIN_COLORS.SELECTOR.LABEL,
  },
  selectorContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 16,
  },
  selectorText: {
    fontFamily: 'System',
    fontWeight: '500',
    fontSize: 16,
    lineHeight: 24,
    color: ADMIN_COLORS.SELECTOR.TEXT,
    flex: 1,
  },
  
  // Scrollable menu area
  menuScrollView: {
    flex: 1,
  },
  menuScrollContent: {
    flexGrow: 1,
  },
  // Navigation menu styles
  navMenu: {
    width: '100%',
    backgroundColor: ADMIN_COLORS.NAVIGATION.BACKGROUND,
    paddingTop: 16,
    paddingHorizontal: 20,
    paddingBottom: 16,
  },
  navListContainer: {
    width: '100%',
  },
  navList: {
    width: '100%',
    gap: 12,
  },
  navMenuItem: {
    width: '100%',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.NAVIGATION.ITEM_BORDER,
    paddingTop: 14,
    paddingHorizontal: 16,
    paddingBottom: 14,
  },
  navMenuItemOpen: {
    backgroundColor: ADMIN_COLORS.NAVIGATION.ITEM_BACKGROUND_OPEN,
    borderWidth: 2,
    borderColor: ADMIN_COLORS.NAVIGATION.ITEM_BORDER_ACTIVE,
    gap: 10,
  },
  navMenuHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 21,
  },
  navMenuTitle: {
    fontFamily: 'System',
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 21,
    color: ADMIN_COLORS.NAVIGATION.TITLE,
  },
  navMenuTitleOpen: {
    fontWeight: '600',
  },
  navMenuDescription: {
    fontFamily: 'System',
    fontWeight: '300',
    fontSize: 12,
    lineHeight: 18,
    color: ADMIN_COLORS.NAVIGATION.DESCRIPTION,
    marginTop: 8,
  },
  navMenuChildren: {
    gap: 8,
    marginTop: 4,
  },
  navMenuChild: {
    paddingVertical: 0,
    position: 'relative',
  },
  navChildContent: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 21,
    justifyContent: 'space-between',
  },
  selectedIndicator: {
    position: 'absolute',
    left: -16,
    top: 0,
    width: 3,
    height: 21,
    backgroundColor: ADMIN_COLORS.NAVIGATION.SELECTED_INDICATOR,
  },
  navMenuChildText: {
    fontFamily: 'System',
    fontWeight: '400',
    fontSize: 14,
    lineHeight: 21,
    color: ADMIN_COLORS.NAVIGATION.CHILD_TEXT,
    width: '100%',
  },
  navMenuChildTextSelected: {
    color: ADMIN_COLORS.NAVIGATION.CHILD_TEXT_SELECTED,
  },
  
  // Bottom section styles - Frame 187 from Figma
  bottomSection: {
    width: '100%',
    gap: 16,
    justifyContent: 'flex-end',
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  toggleText: {
    fontFamily: 'System',
    fontWeight: '600',
    fontSize: 14,
    lineHeight: 21,
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  resioLink: {
    fontFamily: 'System',
    fontWeight: '600',
    fontSize: 14,
    lineHeight: 21,
    color: ADMIN_COLORS.TEXT.ACCENT,
  },
  signOutButton: {
    minWidth: 86,
    height: 40,
    backgroundColor: ADMIN_COLORS.BUTTON.PRIMARY_BACKGROUND,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  signOutText: {
    fontFamily: 'System',
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 21,
    color: ADMIN_COLORS.BUTTON.PRIMARY_TEXT,
    textAlign: 'center',
  },
});
